import React from 'react';

import {
    Chart as ChartJS,
    ChartConfiguration,
    ChartData,
    ChartDataset,
    ChartOptions,
    registerables,
} from 'chart.js';
import {useTranslation} from 'react-i18next';

import {colors} from '@app/styles/theme';
import {MONTH_KEYS} from '@consts/date';

ChartJS.register(...registerables);

interface YearLineChartProps {
    label?: string;
    values?: Array<number | null>;
    datasets?: Array<{
        label: string;
        values: Array<number | null>;
        backgroundColor: string;
        borderColor: string;
    }>;
}

const buildDataset = ({
    label,
    values,
    backgroundColor,
    borderColor,
}: NonNullable<YearLineChartProps['datasets']>[number]): ChartDataset<'line'> => ({
    label,
    data: values,
    cubicInterpolationMode: 'monotone',
    fill: true,
    backgroundColor,
    borderColor,
    borderWidth: 2,
    pointRadius: 5,
    pointBackgroundColor: '#fff',
    pointHoverRadius: 5,
    pointHoverBorderWidth: 2,
    spanGaps: true,
});

export const YearLineChart = ({values = [], label = '', datasets}: YearLineChartProps) => {
    const {t} = useTranslation();
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

    React.useEffect(() => {
        if (!canvasRef.current) return undefined;

        const resolvedDatasets =
            datasets && datasets.length > 0
                ? datasets.map(buildDataset)
                : [
                      buildDataset({
                          label,
                          values,
                          backgroundColor: `${colors.brandPrimary}30`,
                          borderColor: colors.brandPrimary,
                      }),
                  ];

        const chartData: ChartData<'line'> = {
            labels: MONTH_KEYS.map((month) => t(`dictionaries.months.${month}.short`)),
            datasets: resolvedDatasets,
        };

        const chartOptions: ChartOptions<'line'> = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    padding: 16,
                    callbacks: {
                        label() {
                            return '';
                        },
                        beforeLabel(item) {
                            return `${item.dataset.label || label}: ${item.formattedValue}`;
                        },
                    },
                },
            },
        };

        const config: ChartConfiguration<'line'> = {
            type: 'line',
            data: chartData,
            options: chartOptions,
        };

        const chart = new ChartJS(canvasRef.current, config);

        return () => chart.destroy();
    }, [datasets, label, t, values]);

    return <canvas ref={canvasRef} />;
};
